// Partner attribution capture for the co-branded signup pages.
//
// Shaped after lib/referral.ts (same cookie mechanics, same never-throw
// posture) but deliberately a separate module rather than a generalization of
// it — the two carry different values, different validation, and different
// consequences, so folding them together would couple two policies that are
// going to diverge.
//
// Two decisions worth keeping in view:
//
// 1. The cookie holds the partner **id**, never the slug. Slugs are globally
//    unique and immutable today, so a recycled slug cannot re-point old
//    attribution — but storing the id means that guarantee is not the only
//    thing standing between a stale link and the wrong partner.
//
// 2. Capture happens when the visitor actually starts signing up on a partner
//    page, not on mere page view. Referral uses last-click-on-any-pageview
//    because what it grants is a credit bonus. Partner attribution eventually
//    grants a partner's operator `org:admin` on the workspace the visitor
//    creates — with its credentials, conversations and ad data — so "glanced at
//    a partner page once, signed up from the normal entry three weeks later"
//    must not silently produce that outcome.
//
// Everything here is best-effort and never throws: attribution is enrichment
// on the auth flow, and must not be able to break it.

const PARTNER_COOKIE = 'soku_partner'
const PARTNER_TTL_SECONDS = 30 * 24 * 60 * 60 // 30 days

// Canonical UUID shape. The server re-resolves the id against the partners
// table regardless, so this only keeps obvious junk out of the cookie.
const PARTNER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

function normalizeId(value: string | null | undefined): string | undefined {
  const candidate = value?.trim().toLowerCase()
  return candidate && PARTNER_ID_RE.test(candidate) ? candidate : undefined
}

/**
 * Record that this signup is being started from a partner's page.
 *
 * Call at signup initiation — on submitting the email form or following the
 * Google button — not on page load. See the module note on why the timing is
 * tighter than referral's.
 */
export function capturePartnerId(partnerId: string | null | undefined): void {
  if (typeof document === 'undefined') return
  try {
    const id = normalizeId(partnerId)
    if (!id) return
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${PARTNER_COOKIE}=${id}; Max-Age=${PARTNER_TTL_SECONDS}; Path=/; SameSite=Lax${secure}`
  } catch {
    // Cookies can be blocked by privacy settings; attribution must never
    // affect whether the visitor can sign up.
  }
}

/** The captured partner id, if there is a valid one. */
export function getPartnerId(): string | undefined {
  if (typeof document === 'undefined') return undefined
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${PARTNER_COOKIE}=([^;]*)`),
    )
    return normalizeId(match ? decodeURIComponent(match[1]) : undefined)
  } catch {
    return undefined
  }
}
